/**
 * Reusable formula evaluator
 *
 * Supported formulas:
 * - SUM(field1, field2, ...)
 * - COUNT(field1, field2, ...)
 * - AVG(field1, field2, ...)
 *
 * Supports deep nested paths:
 * - section1.q1
 * - mental.health.score
 */

const resolveFieldValue = (path, data) => {
    if (!path || !data) return undefined

    return path.split(".").reduce((acc, key) => acc?.[key], data)
}

const resolveNumericValue = (value) => {
    if (value === undefined || value === null || value === "") {
        return null
    }

    const num = Number(value)

    return Number.isNaN(num) ? null : num
}

const parseFieldRefs = (argsStr = "") => {
    return argsStr
        .split(",")
        .map((item) => item.trim())
        .filter(Boolean)
}

export const evaluateFormula = (formula, data) => {
    if (!formula || typeof formula !== "string") {
        return null
    }

    // Supports:
    // SUM(a,b)
    // COUNT(a,b)
    // AVG(a,b)

    const match = formula.trim().match(/^(SUM|COUNT|AVG)\((.+)\)$/i)

    if (!match) {
        return null
    }

    const operation = match[1].toUpperCase()
    const fields = parseFieldRefs(match[2])

    if (!fields.length) {
        return null
    }

    switch (operation) {
        case "SUM": {
            let total = 0

            fields.forEach((fieldPath) => {
                const value = resolveFieldValue(fieldPath, data)
                const numberValue = resolveNumericValue(value)

                total += numberValue ?? 0
            })

            return total
        }

        case "COUNT": {
            let count = 0

            fields.forEach((fieldPath) => {
                const value = resolveFieldValue(fieldPath, data)

                if (value !== undefined && value !== null && value !== "") {
                    count++
                }
            })

            return count
        }

        case "AVG": {
            let total = 0
            let count = 0

            fields.forEach((fieldPath) => {
                const value = resolveFieldValue(fieldPath, data)
                const numberValue = resolveNumericValue(value)

                if (numberValue !== null) {
                    total += numberValue
                    count++
                }
            })

            if (count === 0) {
                return 0
            }

            return Math.round((total / count) * 100) / 100
        }

        default:
            return null
    }
}
